import React, { useState, useEffect, useMemo, useCallback } from 'react';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showToast } from '@components/Layout/Toast';
import Button from '@components/UI/Button';
import DataTable from '@components/UI/DataTable';
import Modal from '@components/UI/Modal';
import FormInput from '@components/UI/FormInput';
import Card from '@components/UI/Card';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import { MultiSelect, ConfirmationDialog, DeleteButton } from '@components/UI';
import type { Option } from '@components/UI/MultiSelect';
import type { MultiValue, SingleValue } from 'react-select';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTrpcMutation } from '@/api/useTrpcMutation.ts';

// Define EntityType here since we can't import it correctly
type EntityType = 'Asset' | 'Cryptocurrency' | 'AssetIndex';

interface EntityListItem {
    id: number;
    type: EntityType;
}

interface EntityList {
    id: number;
    name: string;
    entities: EntityListItem[];
}

const EntityLists: React.FC = () => {
    // State for error handling
    const [error, setError] = useState<string | null>(null);

    // State for create/edit modal
    const [showEntityListModal, setShowEntityListModal] = useState(false);
    const [currentEntityList, setCurrentEntityList] = useState<EntityList | null>(null);
    const [listName, setListName] = useState('');
    const [listEntities, setListEntities] = useState<Option[]>([]);

    // State for delete confirmation dialog
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [entityListToDelete, setEntityListToDelete] = useState<number | null>(null);

    // Query client for invalidation
    const queryClient = useQueryClient();

    // Fetch entity lists with tRPC
    const entityListsQuery = useAppQuery(
        {
            ...trpc.user.fetchEntityLists.queryOptions(),
            staleTime: 120000, // 2 misnute
            refetchInterval: 30000 // Refetch every 30 seconds
        },
        {
            fallbackErrorMessage: 'Failed to load entity lists',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Fetch all entities with tRPC
    const allEntitiesQuery = useAppQuery(
        {
            ...trpc.entity.fetchAllEntities.queryOptions({})
        },
        {
            fallbackErrorMessage: 'Failed to load entities for selection',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Manage global loading spinner based on query states
    useEffect(() => {
        const isLoading = entityListsQuery.isLoading || allEntitiesQuery.isLoading;

        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        // Always hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, [entityListsQuery.isLoading, allEntitiesQuery.isLoading]);

    // Extract data from queries
    const entityLists = useMemo(() => entityListsQuery.data?.entityLists || [], [entityListsQuery.data?.entityLists]);
    const allEntities = useMemo(() => allEntitiesQuery.data?.entities || [], [allEntitiesQuery.data?.entities]);

    // Create options for MultiSelect
    const allEntityOptions = useMemo(() => {
        return allEntities.map((entity) => ({
            value: `${entity.id.toString()}-${entity.type}`,
            label: `${entity.name} (${entity.symbol})`
        }));
    }, [allEntities]);

    // Loading state
    const loading = entityListsQuery.isLoading || allEntitiesQuery.isLoading;

    // Convert array of entity items to array of options for MultiSelect
    const entitiesToOptions = useCallback(
        (entities: EntityListItem[]): Option[] => {
            // Map entity items to options, using data from allEntities where available
            return entities.map((entity) => {
                const matchingEntity = allEntities.find((e) => e.id === entity.id && e.type === entity.type);

                if (matchingEntity) {
                    return {
                        value: `${entity.id}-${entity.type}`,
                        label: `${matchingEntity.name} (${matchingEntity.symbol})`
                    };
                }

                // Fallback if entity details not found
                return {
                    value: `${entity.id}-${entity.type}`,
                    label: `${entity.id} (${entity.type})`
                };
            });
        },
        [allEntities]
    );

    // Convert array of options to array of entity items
    const optionsToEntities = useCallback((options: Option[]): EntityListItem[] => {
        return options.map((option) => {
            const [id, type] = option.value.toString().split('-');
            return {
                id: parseInt(id),
                type: type as EntityType
            };
        });
    }, []);

    // Function to open create/edit modal
    const openEntityListModal = useCallback(
        (entityList: EntityList | null = null) => {
            if (entityList) {
                // Edit mode
                setCurrentEntityList(entityList);
                setListName(entityList.name);

                // Convert entities to options for MultiSelect
                setListEntities(entitiesToOptions(entityList.entities));
            } else {
                // Create mode
                setCurrentEntityList(null);
                setListName('');
                setListEntities([]);
            }

            setShowEntityListModal(true);
        },
        [entitiesToOptions]
    );

    const modifyEntityListMutation = useTrpcMutation(
        trpc.user.modifyEntityList.mutationOptions({
            // fires once, immediately after a successful delete
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchEntityLists.queryOptions().queryKey
                });
            }
        }),
        {
            successMessage: 'Entity list updated successfully',
            fallbackErrorMessage: 'Failed to update entity list'
        }
    );

    const saveEntityListMutation = useTrpcMutation(
        trpc.user.saveEntityList.mutationOptions({
            // fires once, immediately after a successful delete
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchEntityLists.queryOptions().queryKey
                });
            }
        }),
        {
            successMessage: 'Entity list created successfully',
            fallbackErrorMessage: 'Failed to create entity list'
        }
    );

    // Function to handle form submission - memoized with useCallback
    const handleSaveEntityList = useCallback(() => {
        if (!listName.trim()) {
            showToast('List name is required', 'error');
            return;
        }

        const entities = optionsToEntities(listEntities);

        if (entities.length === 0) {
            showToast('At least one entity is required', 'error');
            return;
        }

        try {
            if (currentEntityList) {
                // Update existing entity list
                modifyEntityListMutation.mutate({
                    listId: currentEntityList.id,
                    listName: listName.trim(),
                    entities
                });
            } else {
                // Create new entity list
                saveEntityListMutation.mutate({
                    name: listName.trim(),
                    entities
                });
            }

            setShowEntityListModal(false);
        } catch (error) {
            console.error('Error saving entity list:', error);
            showToast(`Error ${currentEntityList ? 'updating' : 'creating'} entity list`, 'error');
        }
    }, [listName, listEntities, currentEntityList, optionsToEntities]);

    // Handle MultiSelect change - memoized with useCallback
    const handleMultiSelectChange = useCallback(
        (newValue: MultiValue<Option> | SingleValue<Option>) => {
            if (Array.isArray(newValue)) {
                setListEntities(newValue);
            }
        },
        [setListEntities]
    );

    const deleteItemsMutation = useTrpcMutation(
        trpc.user.deleteEntityList.mutationOptions({
            // fires once, immediately after a successful delete
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchEntityLists.queryOptions().queryKey
                });
            }
        }),
        {
            successMessage: 'Entity list deleted successfully',
            fallbackErrorMessage: 'Failed to delete entity list'
        }
    );

    // Function to open delete confirmation dialog
    const handleDeleteEntityList = useCallback((id: number) => {
        setEntityListToDelete(id);
        setIsDeleteConfirmationOpen(true);
    }, []);

    // Function to confirm deletion
    const handleConfirmDelete = useCallback(() => {
        if (entityListToDelete === null) return;

        try {
            void deleteItemsMutation.mutateAsync({
                id: entityListToDelete
            });
        } catch (error) {
            console.error('Error deleting entity list:', error);
            showToast('Error deleting entity list', 'error');
        }

        setIsDeleteConfirmationOpen(false);
        setEntityListToDelete(null);
    }, [deleteItemsMutation, entityListToDelete]);

    // Function to cancel deletion
    const handleCancelDelete = useCallback(() => {
        setIsDeleteConfirmationOpen(false);
        setEntityListToDelete(null);
    }, []);

    // Table columns for entity lists
    const columns = useMemo<ColumnDef<EntityList>[]>(
        () => [
            {
                header: 'ID',
                accessorKey: 'id',
                size: 95
            },
            {
                header: 'Name',
                accessorKey: 'name',
                size: 700
            },
            {
                header: 'Entities',
                accessorKey: 'entities',
                size: 150,
                cell: ({ getValue }) => {
                    const entities = getValue<EntityListItem[]>();
                    const count = Array.isArray(entities) ? entities.length : 0;
                    return <span>{count} items</span>;
                }
            },
            {
                header: 'Actions',
                size: 175,
                accessorKey: 'actions',
                cell: ({ row }) => {
                    const entityList = row.original;
                    return (
                        <div className="flex space-x-2">
                            <Button size="sm" variant="primary" onClick={() => openEntityListModal(entityList)}>
                                Edit
                            </Button>
                            <DeleteButton size="sm" text="Delete" onClick={() => handleDeleteEntityList(entityList.id)} />
                        </div>
                    );
                }
            }
        ],
        [openEntityListModal, handleDeleteEntityList]
    );

    return (
        <UserLayout title="Entity Lists - Project 314">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold text-white">Entity Lists</h1>
                <Button variant="primary" onClick={() => openEntityListModal()}>
                    Create Entity List
                </Button>
            </div>

            {loading ? (
                <DataLoadingSpinner text="Loading entity lists..." />
            ) : error ? (
                <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
            ) : (
                <div className="w-full">
                    <Card className="mb-4">
                        <div className="w-full overflow-x-auto">
                            <DataTable striped columns={columns} data={entityLists} pagination={{ enabled: true, pageSize: 20 }} />
                        </div>
                    </Card>
                </div>
            )}

            {/* Create/Edit Entity List Modal */}
            <Modal
                footer={
                    <Button variant="primary" onClick={handleSaveEntityList}>
                        Save
                    </Button>
                }
                isOpen={showEntityListModal}
                title={currentEntityList ? 'Edit Entity List' : 'Create Entity List'}
                onClose={() => setShowEntityListModal(false)}
            >
                <div className="space-y-4">
                    <div className="mb-4">
                        <FormInput
                            id="listName"
                            label="Name"
                            placeholder="Entity List Name"
                            value={listName}
                            onChange={(e) => setListName(e.target.value)}
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-zinc-300 mb-2" htmlFor="listEntities">
                            Entities
                        </label>
                        <MultiSelect
                            isClearable
                            isMulti
                            id="listEntities"
                            options={allEntityOptions}
                            placeholder="Search for entities..."
                            value={listEntities}
                            onChange={handleMultiSelectChange}
                        />
                        <p className="text-sm text-zinc-400 mt-1">Search for entities by name or symbol</p>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                confirmText="Delete"
                confirmVariant="danger"
                icon={
                    <svg
                        className="w-6 h-6 text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                        />
                    </svg>
                }
                isOpen={isDeleteConfirmationOpen}
                message="Are you sure you want to delete this entity list? This action cannot be undone."
                title="Confirm Deletion"
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
            />
        </UserLayout>
    );
};

export default EntityLists;
